/**
 * React Query hooks for tier configuration and tenant usage.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTiers,
  getTenantUsage,
  getMyUsage,
  updateTier,
} from '@/api/tiers';
import type { TierConfigUpdateRequest } from '@/types/tier';

const TIERS_KEY = ['tiers'] as const;

export function useTiers() {
  return useQuery({
    queryKey: TIERS_KEY,
    queryFn: getTiers,
  });
}

export function useUpdateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tier, payload }: { tier: string; payload: TierConfigUpdateRequest }) =>
      updateTier(tier, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TIERS_KEY });
      queryClient.invalidateQueries({ queryKey: ['pci-capabilities'] });
      queryClient.invalidateQueries({ queryKey: ['pci-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['my-usage'] });
    },
  });
}

export function useTenantUsage(tenantId: string | null) {
  return useQuery({
    queryKey: ['tenant-usage', tenantId],
    queryFn: () => getTenantUsage(tenantId!),
    enabled: !!tenantId,
  });
}

export function useMyUsage() {
  return useQuery({
    queryKey: ['my-usage'],
    queryFn: getMyUsage,
  });
}
